'use client';
import { useEffect, useState } from 'react';

import { UiModHeadFixed } from '@/fo/ui/mod/head/fixed';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import {
  EVENT_NAME_PRODUCT_INFO_MAIN_HIDE,
  EVENT_NAME_PRODUCT_INFO_MAIN_SHOW
} from '@/fo/ui/product/info/main';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { utilEventElementVisibility } from '@/fo/util/event-element-visibility';
import { MappedProduct } from '@/fo/util/map-product-data';

import { Actions } from '../components/Actions';
import { PriceInfo } from '../components/PriceInfo';
import { ProductInfo } from '../components/ProductInfo';

import { styles } from './FeatureProductHeaderActions.styles';

export interface FeatureProductHeaderActionsProps {
  product: MappedProduct;
  isAuthorized: boolean;
}

export function FeatureProductHeaderActions({
  product: initialProduct,
  isAuthorized
}: FeatureProductHeaderActionsProps) {
  const slots = styles();

  const isDesktop = useMediaQuery('lg');

  const [isHidden, setIsHidden] = useState(true);
  const [product, setProduct] = useState<MappedProduct>(
    initialProduct.configurableVariants?.find((item) => item.isSelected)
      ?.product || initialProduct
  );
  const [configurableVariants, setConfigurableVariants] = useState(
    initialProduct.configurableVariants
  );

  useEffect(() => {
    const unsubscribeOnOpen = utilEventConfigurableProduct.onOpen((product) => {
      setProduct(product);
    });

    const unsubscribeOnProductSelect =
      utilEventConfigurableProduct.onProductSelect((product) => {
        setProduct(product);
        setConfigurableVariants((prev) =>
          prev?.map((option) => ({
            ...option,
            isSelected:
              product.configurableVariants?.find(
                (item) => option.sku === item.sku
              )?.isSelected ?? false
          }))
        );
      });

    const unsubscribeOnShow = utilEventElementVisibility.onShow(
      EVENT_NAME_PRODUCT_INFO_MAIN_SHOW,
      () => {
        setIsHidden(false);
      }
    );

    const unsubscribeOnHide = utilEventElementVisibility.onHide(
      EVENT_NAME_PRODUCT_INFO_MAIN_HIDE,
      () => {
        setIsHidden(true);
      }
    );

    return () => {
      unsubscribeOnOpen();
      unsubscribeOnProductSelect();
      unsubscribeOnShow();
      unsubscribeOnHide();
    };
  }, []);

  return (
    <UiModHeadFixed
      className={slots.base()}
      show={!isHidden && isDesktop}
      positioning="fixed"
      order={1}
    >
      <div className={slots.container()}>
        <ProductInfo
          thumbnail={product.thumbnail}
          name={product.name}
          rating={initialProduct.rating}
          configurableVariants={configurableVariants}
          setConfigurableVariants={setConfigurableVariants}
        />
        <PriceInfo
          basePrice={product.basePrice}
          finalPrice={product.finalPrice}
          stockStatus={product.stockStatus}
          bonusPoints={product.bonusPoints}
          bonusPointsMax={product.bonusPointsMax}
          bonusLabel={product.labelsBonus[0]?.name}
          isAuthorized={isAuthorized}
        />
        <Actions
          product={product}
          parentSku={initialProduct.parentSku}
          isAuthorized={isAuthorized}
        />
      </div>
    </UiModHeadFixed>
  );
}
